import { __decorate, __param } from '../../../../../../../external/tslib/tslib.es6.js';
import { localizeWithPath } from 'monaco-editor/esm/vs/nls.js';
import { clearConfiguredLanguageAssociations, registerConfiguredLanguageAssociation } from 'monaco-editor/esm/vs/editor/common/services/languagesAssociations.js';
import { joinPath } from 'monaco-editor/esm/vs/base/common/resources.js';
import { LanguageService } from 'monaco-editor/esm/vs/editor/common/services/languageService.js';
import { IConfigurationService } from 'monaco-editor/esm/vs/platform/configuration/common/configuration.js';
import { IEnvironmentService } from 'monaco-editor/esm/vs/platform/environment/common/environment.js';
import { FILES_ASSOCIATIONS_CONFIG } from 'monaco-editor/esm/vs/platform/files/common/files.js';
import { IExtensionService } from '../../extensions/common/extensions.js';
import { ExtensionsRegistry } from '../../extensions/common/extensionsRegistry.js';
import { ILogService } from 'monaco-editor/esm/vs/platform/log/common/log.js';
const languagesExtPoint = ( ExtensionsRegistry.registerExtensionPoint({
    extensionPoint: 'languages',
    jsonSchema: {
        description: ( localizeWithPath(
            'vs/workbench/services/language/common/languageService',
            'vscode.extension.contributes.languages',
            'Contributes language declarations.'
        )),
        type: 'array',
        items: {
            type: 'object',
            defaultSnippets: [{ body: { id: '${1:languageId}', aliases: ['${2:label}'], extensions: ['${3:extension}'], configuration: './language-configuration.json' } }],
            properties: {
                id: {
                    description: ( localizeWithPath(
                        'vs/workbench/services/language/common/languageService',
                        'vscode.extension.contributes.languages.id',
                        'ID of the language.'
                    )),
                    type: 'string'
                },
                aliases: {
                    description: ( localizeWithPath(
                        'vs/workbench/services/language/common/languageService',
                        'vscode.extension.contributes.languages.aliases',
                        'Name aliases for the language.'
                    )),
                    type: 'array',
                    items: {
                        type: 'string'
                    }
                },
                extensions: {
                    description: ( localizeWithPath(
                        'vs/workbench/services/language/common/languageService',
                        'vscode.extension.contributes.languages.extensions',
                        'File extensions associated to the language.'
                    )),
                    default: ['.foo'],
                    type: 'array',
                    items: {
                        type: 'string'
                    }
                },
                filenames: {
                    description: ( localizeWithPath(
                        'vs/workbench/services/language/common/languageService',
                        'vscode.extension.contributes.languages.filenames',
                        'File names associated to the language.'
                    )),
                    type: 'array',
                    items: {
                        type: 'string'
                    }
                },
                filenamePatterns: {
                    description: ( localizeWithPath(
                        'vs/workbench/services/language/common/languageService',
                        'vscode.extension.contributes.languages.filenamePatterns',
                        'File name glob patterns associated to the language.'
                    )),
                    type: 'array',
                    items: {
                        type: 'string'
                    }
                },
                mimetypes: {
                    description: ( localizeWithPath(
                        'vs/workbench/services/language/common/languageService',
                        'vscode.extension.contributes.languages.mimetypes',
                        'Mime types associated to the language.'
                    )),
                    type: 'array',
                    items: {
                        type: 'string'
                    }
                },
                firstLine: {
                    description: ( localizeWithPath(
                        'vs/workbench/services/language/common/languageService',
                        'vscode.extension.contributes.languages.firstLine',
                        'A regular expression matching the first line of a file of the language.'
                    )),
                    type: 'string'
                },
                configuration: {
                    description: ( localizeWithPath(
                        'vs/workbench/services/language/common/languageService',
                        'vscode.extension.contributes.languages.configuration',
                        'A relative path to a file containing configuration options for the language.'
                    )),
                    type: 'string',
                    default: './language-configuration.json'
                },
                icon: {
                    type: 'object',
                    description: ( localizeWithPath(
                        'vs/workbench/services/language/common/languageService',
                        'vscode.extension.contributes.languages.icon',
                        'A icon to use as file icon, if no icon theme provides one for the language.'
                    )),
                    properties: {
                        light: {
                            description: ( localizeWithPath(
                                'vs/workbench/services/language/common/languageService',
                                'vscode.extension.contributes.languages.icon.light',
                                'Icon path when a light theme is used'
                            )),
                            type: 'string'
                        },
                        dark: {
                            description: ( localizeWithPath(
                                'vs/workbench/services/language/common/languageService',
                                'vscode.extension.contributes.languages.icon.dark',
                                'Icon path when a dark theme is used'
                            )),
                            type: 'string'
                        }
                    }
                }
            }
        }
    },
    activationEventsGenerator: (languageContributions, result) => {
        for (const languageContribution of languageContributions) {
            if (languageContribution.id && languageContribution.configuration) {
                result.push(`onLanguage:${languageContribution.id}`);
            }
        }
    }
}));
let WorkbenchLanguageService = class WorkbenchLanguageService extends LanguageService {
    constructor(extensionService, configurationService, environmentService, logService) {
        super(environmentService.verbose || environmentService.isExtensionDevelopment || !environmentService.isBuilt);
        this.logService = logService;
        this._configurationService = configurationService;
        this._extensionService = extensionService;
        languagesExtPoint.setHandler((extensions) => {
            const allValidLanguages = [];
            for (let i = 0, len = extensions.length; i < len; i++) {
                const extension = extensions[i];
                if (!Array.isArray(extension.value)) {
                    extension.collector.error(( localizeWithPath(
                        'vs/workbench/services/language/common/languageService',
                        'invalid',
                        "Invalid `contributes.{0}`. Expected an array.",
                        languagesExtPoint.name
                    )));
                    continue;
                }
                for (let j = 0, lenJ = extension.value.length; j < lenJ; j++) {
                    const ext = extension.value[j];
                    if (isValidLanguageExtensionPoint(ext, extension.description, extension.collector)) {
                        let configuration = undefined;
                        if (ext.configuration) {
                            configuration = joinPath(extension.description.extensionLocation, ext.configuration);
                        }
                        allValidLanguages.push({
                            id: ext.id,
                            extensions: ext.extensions,
                            filenames: ext.filenames,
                            filenamePatterns: ext.filenamePatterns,
                            firstLine: ext.firstLine,
                            aliases: ext.aliases,
                            mimetypes: ext.mimetypes,
                            configuration: configuration,
                            icon: ext.icon && {
                                light: joinPath(extension.description.extensionLocation, ext.icon.light),
                                dark: joinPath(extension.description.extensionLocation, ext.icon.dark)
                            }
                        });
                    }
                }
            }
            this._registry.setDynamicLanguages(allValidLanguages);
        });
        this.updateMime();
        this._configurationService.onDidChangeConfiguration(e => {
            if (e.affectsConfiguration(FILES_ASSOCIATIONS_CONFIG)) {
                this.updateMime();
            }
        });
        this._extensionService.whenInstalledExtensionsRegistered().then(() => {
            this.updateMime();
        });
        this._register(this.onDidRequestRichLanguageFeatures((languageId) => {
            this._extensionService.activateByEvent(`onLanguage:${languageId}`);
            this._extensionService.activateByEvent(`onLanguage`);
        }));
    }
    updateMime() {
        const configuration = this._configurationService.getValue();
        clearConfiguredLanguageAssociations();
        if (configuration.files?.associations) {
            ( Object.keys(configuration.files.associations)).forEach(pattern => {
                const langId = configuration.files.associations[pattern];
                if (typeof langId !== 'string') {
                    this.logService.warn(`Ignoring configured 'files.associations' for '${pattern}' because its type is not a string but '${typeof langId}'`);
                    return;
                }
                const mimeType = this.getMimeType(langId) || `text/x-${langId}`;
                registerConfiguredLanguageAssociation({ id: langId, mime: mimeType, filepattern: pattern });
            });
        }
        this._onDidChange.fire();
    }
};
WorkbenchLanguageService = ( __decorate([
    ( __param(0, IExtensionService)),
    ( __param(1, IConfigurationService)),
    ( __param(2, IEnvironmentService)),
    ( __param(3, ILogService))
], WorkbenchLanguageService));
function isUndefinedOrStringArray(value) {
    if (typeof value === 'undefined') {
        return true;
    }
    if (!Array.isArray(value)) {
        return false;
    }
    return value.every(item => typeof item === 'string');
}
function isValidLanguageExtensionPoint(value, extension, collector) {
    if (!value) {
        collector.error(( localizeWithPath(
            'vs/workbench/services/language/common/languageService',
            'invalid.empty',
            "Empty value for `contributes.{0}`",
            languagesExtPoint.name
        )));
        return false;
    }
    if (typeof value.id !== 'string') {
        collector.error(( localizeWithPath(
            'vs/workbench/services/language/common/languageService',
            'require.id',
            "property `{0}` is mandatory and must be of type `string`",
            'id'
        )));
        return false;
    }
    if (!isUndefinedOrStringArray(value.extensions)) {
        collector.error(( localizeWithPath(
            'vs/workbench/services/language/common/languageService',
            'opt.extensions',
            "property `{0}` can be omitted and must be of type `string[]`",
            'extensions'
        )));
        return false;
    }
    if (!isUndefinedOrStringArray(value.filenames)) {
        collector.error(( localizeWithPath(
            'vs/workbench/services/language/common/languageService',
            'opt.filenames',
            "property `{0}` can be omitted and must be of type `string[]`",
            'filenames'
        )));
        return false;
    }
    if (typeof value.firstLine !== 'undefined' && typeof value.firstLine !== 'string') {
        collector.error(( localizeWithPath(
            'vs/workbench/services/language/common/languageService',
            'opt.firstLine',
            "property `{0}` can be omitted and must be of type `string`",
            'firstLine'
        )));
        return false;
    }
    if (typeof value.configuration !== 'undefined' && typeof value.configuration !== 'string') {
        collector.error(( localizeWithPath(
            'vs/workbench/services/language/common/languageService',
            'opt.configuration',
            "property `{0}` can be omitted and must be of type `string`",
            'configuration'
        )));
        return false;
    }
    if (!isUndefinedOrStringArray(value.aliases)) {
        collector.error(( localizeWithPath(
            'vs/workbench/services/language/common/languageService',
            'opt.aliases',
            "property `{0}` can be omitted and must be of type `string[]`",
            'aliases'
        )));
        return false;
    }
    if (!isUndefinedOrStringArray(value.mimetypes)) {
        collector.error(( localizeWithPath(
            'vs/workbench/services/language/common/languageService',
            'opt.mimetypes',
            "property `{0}` can be omitted and must be of type `string[]`",
            'mimetypes'
        )));
        return false;
    }
    if (typeof value.icon !== 'undefined') {
        if (typeof value.icon !== 'object' || typeof value.icon.light !== 'string' || typeof value.icon.dark !== 'string') {
            collector.error(( localizeWithPath(
                'vs/workbench/services/language/common/languageService',
                'opt.icon',
                "property `{0}` can be omitted and must be of type `object` with properties `{1}` and `{2}` of type `string`",
                'icon',
                'light',
                'dark'
            )));
            return false;
        }
    }
    return true;
}
export { WorkbenchLanguageService, languagesExtPoint };
